import {
  Component,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AfricaMapComponent } from '../../africa-map/africa-map.component';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import {
  IgxCategoryChartModule,
  IgxPieChartModule,
  IgxItemLegendModule,
  IgxLegendModule,
  IgxCategoryChartComponent,
  IgxDataLegendComponent
} from 'igniteui-angular-charts';
import { ReportsIntergrationService } from '../../services/reports-intergration.service';
import {
  CompletionTableData,
  MAndEData
} from '../../models/studentProfileModels';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';
import { HttpHelperService } from '../../services/helper.service';
import { DateRangeDialogComponent } from '../../date-range-dialog/date-range-dialog.component';
import { ReportsBaseService } from '../../services/reports-base.service';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TooltipService } from '../../services/tooltip.service';
import { ChartJsComponent } from '../../shared/chart-js/chart-js.component';

@Component({
  selector: 'app-kitso-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule,
    MatTableModule,
    IgxCategoryChartModule,
    IgxPieChartModule,
    IgxItemLegendModule,
    IgxLegendModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule,
    AfricaMapComponent,
    ChartJsComponent,
  ],
  templateUrl: './kitso-report.component.html',
  styleUrl: './kitso-report.component.scss',
})
export class KitsoReportComponent implements OnInit {
  mAndEData: MAndEData[] = [];
  trend: { percentage: number; color: string } | null = null;

  constructor(
    private reportsService: ReportsIntergrationService,
    private authService: KeycloakAuthService,
    private helperService: HttpHelperService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public baseService: ReportsBaseService,
    public tooltips: TooltipService,
  ) { }

  ngOnInit(): void {
    this.baseService.updateDateRange();
    this.checkUserBotAccess();
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      data: { startDate: this.baseService.startDate, endDate: this.baseService.endDate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.baseService.startDate = result.startDate;
        this.baseService.endDate = result.endDate;
        this.onDateChange();
      }
    });
  }

  private checkUserBotAccess(): void {
    this.baseService.isLoading = true;
    this.baseService.error = null;

    const user = this.authService.currentUserValue();
    if (user && user.roles) {
      this.processUserBotAccess(user);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((u: any) => !!u && !!u.roles),
        take(1)
      )
      .subscribe({
        next: (u: any) => {
          this.processUserBotAccess(u);
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.baseService.error = "Failed to load user data. Please try logging in again.";
          this.baseService.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processUserBotAccess(user: any): void {
    const botRoles = user.roles.filter((role: string) => role.startsWith('bot_access_'));
    this.baseService.availableBots = botRoles.map((role: string) => {
      const botName = role.replace('bot_access_', '');
      return {
        key: botName as 'kitso',
        name: botName.charAt(0).toUpperCase() + botName.slice(1)
      };
    });


    this.baseService.availableBots = this.baseService.availableBots.filter(bot => bot.key === 'kitso');

    if (this.baseService.availableBots.length > 0) {
      this.baseService.selectedBot = 'kitso';
      this.loadAllData();
    } else {
      this.baseService.error = "You do not have access to Kitso bot. Please contact an administrator.";
    }
    this.baseService.isLoading = false;
    this.cdr.detectChanges();
  }

  onDateChange(): void {
    this.baseService.clearData();
    this.baseService.updateDateRange();
    if (this.baseService.selectedBot) {
      this.loadAllData();
    }
  }

  onProvinceFilterChange(): void {
    this.baseService.updateProvinceChartForFilter();
    this.cdr.detectChanges();
  }

  onLanguageFilterChange(): void {
    this.baseService.updateLanguageChartForFilter();
    this.cdr.detectChanges();
  }

  private buildPrePostQuizChartData() {
    const stats = this.baseService.currentPeriodStats;
    if (!stats || stats.length === 0) {
      this.baseService.prePostQuizQuestionsChartData = null;
      this.baseService.prePostQuizQuestionsIndexData = [];
      return;
    }
    // Extract data
    this.baseService.prePostQuizQuestions = stats.map(q => q.quizQuestion);

    const preData = stats.map(q => parseFloat(q.pre_learning_percentage) || 0);
    const postData = stats.map(q => parseFloat(q.post_learning_percentage) || 0);
    // Colors - customize as needed
    const preColor = 'rgba(235, 182, 6, 1)';    // Yellow for Pre
    const postColor = 'rgba(62, 132, 220, 1)';   // Blue for Post

    this.baseService.prePostQuizQuestionsChartData = {
      labels: this.baseService.prePostQuizQuestions,
      datasets: [
        {
          label: 'Pre-Learning',
          data: preData,
          backgroundColor: Array(preData.length).fill(preColor),
          borderColor: Array(preData.length).fill(preColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        },
        {
          label: 'Post-Learning',
          data: postData,
          backgroundColor: Array(postData.length).fill(postColor),
          borderColor: Array(postData.length).fill(postColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        }
      ]
    };
    // Legend data
    this.baseService.prePostQuizQuestionsIndexData = [
      { quiz: 'Pre-Learning', color: preColor },
      { quiz: 'Post-Learning', color: postColor },
    ];
  }


  buildAveragePostandPreQuizScores(): void {
    const avgPreScore = this.baseService.getAveragePreQuizScore();
    const avgPostScore = this.baseService.getAveragePostQuizScore();
    const avgEngagementScore = this.baseService.getOverallEngagementStats();
    const avgDropOff = this.baseService.calculateDropOffStats();
    // Build doughnut data for pre-quiz (one value as progress ring)
    this.baseService.preQuizScoresChartData = {
      labels: ['Average Pre-Quiz Score', 'Remaining'],
      datasets: [{
        data: [avgPreScore, 100 - avgPreScore], // e.g., [75, 25]
        backgroundColor: [`${this.baseService.pieChartColor['preQuiz']}`, '#E0E0E0'],
        label: 'Average Pre-Quiz Score'
      }]
    };


    this.baseService.postQuizScoresChartData = {
      labels: ['Average Post-Quiz Score', 'Remaining'],
      datasets: [{
        data: [avgPostScore, 100 - avgPostScore],
        backgroundColor: [`${this.baseService.pieChartColor['postQuiz']}`, '#E0E0E0'],
        label: 'Average Post-Quiz Score'
      }]
    };

    this.baseService.overAllEngagementChartData = {
      labels: ['Engagement ', 'Remaining'],
      datasets: [{
        data: [avgEngagementScore, 100 - avgEngagementScore],
        backgroundColor: [`${this.baseService.pieChartColor['overAllEngagement']}`, '#E0E0E0'],
        label: 'Average Engagement Score'
      }]
    };

    this.baseService.dropOffChartData = {
      labels: ['Drop Off ', 'Remaining'],
      datasets: [{
        data: [avgDropOff, 100 - avgDropOff],
        backgroundColor: [`${this.baseService.pieChartColor['overAllEngagement']}`, '#E0E0E0'],
        label: 'Drop-Off Percentage'
      }]
    };
  }

  buildMAndEChartData() {
    this.baseService.mAndEChartData = this.baseService.buildFeedbackRatingOverTimeChartData(
      this.baseService.mAndEAverages
    );
  }

  private loadAllData(): Promise<void> {
    const localBot = this.baseService.selectedBot;
    if (!localBot) return Promise.resolve();

    this.baseService.isLoading = true;
    const currentStart = this.baseService.formatDate(this.baseService.startDate);
    const currentEnd = this.baseService.formatDate(this.baseService.endDate);
    const { prevStart, prevEnd } = this.baseService.getPreviousPeriod(this.baseService.startDate, this.baseService.endDate);

    const quizStats = this.baseService.loadPrePostQuizStats(localBot, currentStart, currentEnd);
    const engagement = this.baseService.loadEngagementCount(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const completion = this.baseService.loadCompletionStats(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const mAndEStats = this.baseService.loadMAndEStats(localBot, currentStart, currentEnd);
    const mAndENumber = this.baseService.loadMAndENumber(localBot, currentStart, currentEnd);

    return Promise.all([
      this.baseService.loadRemainingData(currentStart, currentEnd),
      this.baseService.loadBreakdowns(localBot, currentStart, currentEnd),
      this.baseService.loadTables(localBot, currentStart, currentEnd, prevStart, prevEnd),

      quizStats.then(() => {
        this.buildPrePostQuizChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([quizStats, engagement, completion]).then(() => {
        this.buildAveragePostandPreQuizScores();
        this.cdr.detectChanges();
      }),

      Promise.all([mAndEStats, mAndENumber]).then(() => {
        this.baseService.updateMAndEConfig();
        this.baseService.mAndEConfigArray = [
          {
            title: 'Total Responses',
            description: 'Total Learners who rated the bot.',
            image: 'totalResponsesImage',
            numberMatch: this.baseService.totalParticipants.toString() || '0'
          },
          {
            title: 'Average Rating',
            description: 'The combined average rating over time.',
            image: 'averageRateImage',
            numberMatch: this.baseService.totalAverage.toString() || '0/5'
          },
          {
            title: 'Participation Rate',
            description: 'Total percentage of learners who participated in rating feedback.',
            image: 'participationRateImage',
            numberMatch: this.baseService.currentParticipation ? `${Math.round(this.baseService.currentParticipation.participationRate)}%` : '0%',
            color: this.baseService.currentParticipation?.color
          },
          {
            title: 'Engagement Trend',
            description: 'Percentage change in participation rate over time.',
            image: 'engagementTrendImage',
            numberMatch: this.baseService.trendPercentage.toString() || '0%',
            color: this.baseService.trendColor
          }
        ];
        this.buildMAndEChartData();
        this.mAndEData = this.baseService.mAndEResponses;
        this.cdr.detectChanges();
      }),
    ])
    .then(() => {
      this.baseService.isLoading = false;
      this.cdr.markForCheck();
    })
    .catch((error) => {
      console.error('Error in loadAllData chain:', error);
      this.baseService.isLoading = false;
      this.baseService.error = 'Failed to load report data';
      this.cdr.detectChanges();
    });
  }

  // Lwazi-specific clear (if needed, but use base)
  private clearData(): void {
    this.baseService.clearData();
  }
}
